import styles from './salePDFPage.module.css'
import { Customer, SaleOrder } from "../../../state/types"
import { useEffect, useMemo, useState } from 'react'
import { DocumentKey, Employee, PaymentType, isSelectablePaymentType, useEmployeeLoaders, usePaymentTypeLoaders } from '../../../../administration'
import { useProductHooks, useProductLoaders } from '../../../../products'
import { Labels, useDataTypesHooks } from '../../../../../features/data-types'
import { useBranchLoaders } from '../../../../manager'
import { Item, PriceBreakdown, usePriceOutlineHooks } from '../../../../operations'
import { PDFHeader } from '../../../../../features/ui'
import { SaleItemPDFRow } from './types'
import { useCustomerLoaders } from '../../../loaders/customerLoaders'
import { useCustomerTypeLoaders } from '../../../loaders/customerTypeLoaders'
import { TableName } from '../../../../../tables'
import { firstItem } from '../../../../../utils/listUtils'
import { Address, addressHelpers } from '../../../../../features/address'
import { toDate } from '../../../../../utils/typeUtils'
import { useSalesHooks } from '../../../hooks/salesHooks'
import { useSaleOperationHooks } from '../../../hooks/saleOperationHooks'
import { DeliveryMethod, DeliveryOrder, useDeliveryHooks, useDeliveryOrderLoaders } from '../../../../deliveries'

type Props = {
    pageIndex: number
    sale: SaleOrder
    items: Item[]
    pagesAmount: number
}

export const SalePDFPage = (props: Props) => {
    const { pageIndex, sale, items, pagesAmount } = props
    const isLastPage = pageIndex === pagesAmount - 1
    
    const [customer, setCustomer] = useState<Customer>()
    const [seller, setSeller] = useState<Employee>()
    const [deliveryOrders, setDeliveryOrders] = useState<DeliveryOrder[]>([])
    const [deliveryAddresses, setDeliveryAddresses] = useState<Address[]>([])
    const [rows, setRows] = useState<SaleItemPDFRow[]>([])

    const { describeAddress } = addressHelpers
    const { formatDate, formatDateTime, formatPrice, formatIdentifier, formatPhone, formatPercentage } = useDataTypesHooks()
    const { describeMeasure, describeQuantity } = useProductHooks()
    const { getPriceOutline, getPriceOutlineLive,  } = usePriceOutlineHooks()
    const { getDeliveryAddresses } = useDeliveryHooks()
    const { getDocumentFooter, getDocumentPaymentTypeIdsData } = useSalesHooks()
    const { describePaymentItemName } = useSaleOperationHooks()

    const branchLoaders = useBranchLoaders()
    const productLoaders = useProductLoaders()
    const customerLoaders = useCustomerLoaders()
    const customerTypeLoaders = useCustomerTypeLoaders()
    const { findCustomerType } = customerTypeLoaders()
    const employeeLoaders = useEmployeeLoaders()
    const paymentTypeLoaders = usePaymentTypeLoaders()
    const deliveryOrderLoaders = useDeliveryOrderLoaders()

    const branch = useMemo(() => branchLoaders().findBranch(sale.branchId)!, [sale])
    const documentFooter = useMemo(() => getDocumentFooter(DocumentKey.SALE), [getDocumentFooter])
    const documentPaymentTypeIdsData = useMemo(() => getDocumentPaymentTypeIdsData(DocumentKey.SALE), [getDocumentPaymentTypeIdsData])
    const priceOutline = useMemo(() => getPriceOutline(TableName.SALE_ORDERS, sale), [sale])
    const itemsPriceOutline = useMemo(() => getPriceOutlineLive(items), [sale, items])

    const priceTuples = useMemo(() => {
        const customerTypeIdData = documentPaymentTypeIdsData?.[sale.customerTypeId]
        const paymentTypeIds = !customerTypeIdData?.latePaymentOnly || sale.latePayment ? customerTypeIdData?.paymentTypeIds || [] : []
        return paymentTypeLoaders().findAllPaymentTypes()
            .filter(paymentType => paymentTypeIds.includes(paymentType.id) && paymentType.id !== sale.paymentTypeId && isSelectablePaymentType(paymentType))
            .map(paymentType => [paymentType, getPriceOutline(TableName.SALE_ORDERS, sale, paymentType.id).total])
    }, [sale, documentPaymentTypeIdsData])

    useEffect(() => {
        employeeLoaders().loadEmployee(sale.sellerId).then(setSeller)
        customerLoaders().loadCustomer(sale.customerId).then(setCustomer)
        deliveryOrderLoaders().loadOperationDeliveryOrders(sale.id).then(setDeliveryOrders)
        getDeliveryAddresses(sale).then(setDeliveryAddresses)
    }, [sale])

    const deliveryMethod = useMemo(() => deliveryOrders.length === 1 ? firstItem(deliveryOrders)!.method : sale.deliveryData?.method, [deliveryOrders])
    const deliveryDate = useMemo(() => firstItem(deliveryOrders)?.deliveryDate, [deliveryOrders])

    // Until a delivery is emitted, the address comes from the sale delivery data
    const deliveryAddress = useMemo(() => {
        if (deliveryAddresses.length > 0) return firstItem(deliveryAddresses)
        const { deliveryData } = sale
        if (deliveryData?.method === DeliveryMethod.SHIPMENT) {
            return customer?.deliveryAddresses.find(address => address.id === deliveryData.addressId)
        }
        if (deliveryData?.method === DeliveryMethod.PICKUP) {
            return branch.address
        }
    }, [deliveryAddresses, sale, customer, branch])

    const customerAddress = customer?.mainAddress
        ? `${customer.mainAddress.address}${customer.mainAddress.betweenStreets ? ` (e/ ${customer.mainAddress.betweenStreets})` : ''}, ${customer.mainAddress.locality}, ${customer.mainAddress.province}`
        : '-'

    const columns = [
        { name: 'product', label: 'Producto' },
        { name: 'measure', label: 'Medida' },
        { name: 'unitTotal', label: 'P. Unitario' },
        { name: 'quantity', label: 'Cantidad' },
        { name: 'total', label: 'P. Total' }
    ]

    const loadRows = async () => {
        const productIds = items.map(item => item.productId)
        const { loadProducts } = productLoaders()
        const products = await loadProducts(productIds)
        return items.map(item => {
            const product = products.find(product => product.id === item.productId)!
            const itemPriceOutline = itemsPriceOutline.items.find(itemPriceOutline => itemPriceOutline.productId === product.id)!
            return {
                product: product.name,
                measure: describeMeasure(product),
                unitSubtotal: formatPrice(itemPriceOutline.unitSubtotal),
                unitTotal: formatPrice(itemPriceOutline.unitTotal),
                quantity: describeQuantity(item.quantity, product),
                total: formatPrice(itemPriceOutline.total)
            }
        })
    }

    useEffect(() => {
        loadRows().then(newRows => setRows(newRows))
    }, [sale, items, itemsPriceOutline])

    return (
        <div className={styles.container}>
            <div className={styles.body}>
                <PDFHeader noValidTicketLabel />

                {pageIndex === 0 && (<>
                    <div className={styles.row}>
                        <span className={styles.saleTitle}>
                            Venta #{sale.code}
                        </span>
                        <div className={styles.date}>
                            Fecha: {formatDateTime(new Date(sale.creationDate))}
                        </div>
                    </div>

                    <div className={`${styles.row} ${styles.alignStart}`}>
                        <div className={styles.infoSection}>
                            <span>Cliente: {customer?.name}</span>
                            <span>Dirección: {customerAddress}</span>
                            <span>DNI/CUIT: {customer ? formatIdentifier(customer.identifier) : '-'}</span>
                            <span>Teléfono: {customer && customer.phones.length > 0 ? formatPhone(firstItem(customer!.phones)) : '-'}</span>
                        </div>
                        <div className={`${styles.infoSection} ${styles.rightSection}`}>
                            <span>Vendedor: {seller?.name}</span>
                            <span>Teléfono: {seller?.phone ? formatPhone(seller.phone) : '-'}</span>
                        </div>
                    </div>

                    <div className={`${styles.row} ${styles.alignStart}`}>
                        <div className={styles.infoSection}>
                            {deliveryMethod && (
                                <span>Método de Entrega: {Labels.deliveryMethodLabel(deliveryMethod)}</span>
                            )}
                            <span>Dirección: {deliveryAddress ? describeAddress(deliveryAddress) : '-'}</span>
                        </div>
                        <div className={`${styles.infoSection} ${styles.rightSection}`}>
                            <span>Sucursal: {branch.name}</span>
                            <span>Fecha de Entrega est.: {formatDate(toDate(deliveryDate))}</span>
                        </div>
                    </div>

                    <div className={`${styles.row} ${styles.alignStart}`}>
                        <div className={styles.infoSection}>
                            <span>
                                {`Medio${sale.paymentItems.length > 1 ? 's' : ''} de Pago: `}
                                {sale.paymentItems.length > 0 ?
                                    sale.paymentItems.map(paymentItem => {
                                        const name = describePaymentItemName(paymentItem)
                                        const amount = sale.paymentItems.length > 1 ? ` (${formatPrice(paymentItem.amount)})` : ''
                                        return `${name}${amount}`
                                    }).join(', ') :
                                    paymentTypeLoaders().findPaymentType(sale.paymentTypeId)?.name || 'Sin medio de pago'
                                }
                            </span>
                        </div>
                        <div className={`${styles.infoSection} ${styles.rightSection}`}>
                            <span>Lista de Precios: {findCustomerType(sale.customerTypeId)!.name}</span>
                        </div>
                    </div>
                </>)}

                <div className={styles.tableRow}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                {columns.map((column, index) => (
                                    <th key={index}>
                                        {column.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, index) => (
                                <tr key={index}>
                                    {columns.map((column, index) => (
                                        <td key={index}>
                                            {row[column.name as keyof SaleItemPDFRow]}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className={styles.pagination}>
                    {pageIndex + 1} / {pagesAmount}
                </div>

                {isLastPage && (
                    <div className={`${styles.row} ${styles.priceOutline}`}>
                        <PriceBreakdown data={priceOutline} showDetail={false} />

                        {priceTuples.map(priceTuple => {
                            const paymentType = priceTuple[0] as PaymentType
                            const price = priceTuple[1] as number
                            return (
                                <div key={paymentType.id} className={styles.totalLine}>
                                    Precio {paymentType.name} {!paymentType.name.includes('%') ? ` (${formatPercentage(paymentType.yield)})` : ''}: <strong>{formatPrice(price)}</strong>
                                </div>
                            )
                        })}
                    </div>
                )}

                <div className={styles.footer}>
                    {isLastPage && sale.notes && (
                        <div className={styles.row}>
                            <span className={styles.notes}>Notas: {sale.notes}</span>
                        </div>
                    )}
                    <div className={styles.separator} />
                    <div className={`${styles.row} ${styles.footerRow}`}>
                        <span>
                            Sucursal {branch.name}: {describeAddress(branch.address)}{branch.email ? ` - Mail: ${branch.email}` : ''}
                        </span>
                        <span>
                            {documentFooter}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    )
}
